import {
  ALLOWED_APPS_MIN_QUERY_LENGTH,
  AUTO_COMPLETION_TAGS,
  KEY_BOARD
} from 'constants/general';
import { ATTRIB } from 'constants/test';
import { WithContext as ReactTags } from 'react-tag-input';
import { AutoCompletionTag } from 'interfaces/common.interface';
import { DEFAULT_HTML_ATTRIBUTES } from 'assets/data';
import CloseCircle from 'assets/icons/CloseCircle';
import COLOR from 'constants/color';

interface AutoCompletionTagsProps {
  tags: AutoCompletionTag[];
  suggestions: AutoCompletionTag[];
  setTags: (arg: AutoCompletionTag[]) => void;
  handleAddTag: (arg: AutoCompletionTag) => void;
  placeholder?: string;
  containerStyles?: string;
  disabled?: boolean;
  allowDragDrop?: boolean;
}

export const AutoCompletionTags = ({
  tags,
  setTags,
  handleAddTag,
  suggestions,
  placeholder,
  containerStyles,
  disabled = false,
  allowDragDrop = true
}: AutoCompletionTagsProps) => {
  return (
    <div className={containerStyles ?? 'w-full h-fit'}>
      <ReactTags
        data-test={ATTRIB.TEST.INPUT_APPLICATIONS}
        minQueryLength={ALLOWED_APPS_MIN_QUERY_LENGTH}
        tags={tags.map((tag) => ({ ...tag, className: '' }))}
        suggestions={suggestions.map((tag) => ({ ...tag, className: '' }))}
        delimiters={[KEY_BOARD.ENTER.CODE]}
        handleDrag={(app, currPosition: number, newPosition: number) => {
          // splice() returns the REMOVED items and mutates in place, so chaining
          // the two calls collapsed the whole list down to the dragged item �
          // dragging one app over another wiped the list (#2160). Clone first,
          // then apply both splices to the same array.
          const reorderedApps = [...tags];
          reorderedApps.splice(
            currPosition,
            AUTO_COMPLETION_TAGS.DELETE_OLD_POSITION_TAG_COUNT
          );
          reorderedApps.splice(
            newPosition,
            AUTO_COMPLETION_TAGS.DELETE_NEW_POSITION_TAG_COUNT,
            { id: app.id, text: app.text }
          );
          setTags(reorderedApps);
        }}
        handleDelete={(id: number) => {
          const filteredApps = (tags ?? []).filter((_, idx) => idx !== id);
          setTags(filteredApps);
        }}
        handleAddition={(app) =>
          handleAddTag({ id: app.id, text: app['text'] })
        }
        inputFieldPosition='top'
        autocomplete
        placeholder={placeholder}
        classNames={{
          tags: 'w-full',
          tagInputField: `w-full px-1.5 py-2 outline-none rounded-t ~text-xs/sm font-medium dark:bg-secondary-dark-300 dark:text-secondary-dark-900 bg-white ${tags.length && 'p-2'} text-black`,
          selected: `w-full h-fit max-h-24 flex flex-wrap gap-x-2.5 gap-y-2 rounded-b ~text-xs/sm dark:bg-secondary-dark-300 dark:text-secondary-dark-900 bg-white ${tags.length && 'p-2'} text-black overflow-y-auto scroll-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-secondary-dark-500 border-t border-gray-400 dark:border-secondary-dark-500`,
          tag: 'dark:text-secondary-dark-900 dark:bg-secondary-dark-400 text-white bg-gray-600 pl-2 pr-1 py-0.5 rounded-md flex items-center gap-1',
          suggestions:
            'w-full h-fit max-h-20 overflow-y-auto scroll-thin scrollbar-thumb-gray-400 dark:scrollbar-thumb-secondary-dark-500 text-xs py-1.5 rounded-b-md dark:bg-secondary-dark-200 dark:text-secondary-dark-900 bg-gray-50 text-black px-1.5',
          activeSuggestion: 'active-suggestion'
        }}
        allowDragDrop={allowDragDrop}
        inputProps={{ ...DEFAULT_HTML_ATTRIBUTES, spellCheck: 'false' }}
        readOnly={disabled}
        removeComponent={(event: { index: number }) => (
          <button
            onClick={() => {
              setTags(tags.filter((_, index) => event.index !== index));
            }}
            className='bg-secondary-50 dark:bg-secondary-dark-400 rounded-full'>
            <CloseCircle
              styles='~w-3/4 ~h-3/4'
              fill={COLOR.SEMANTIC.ERROR}
              fillDarkMode={COLOR.SEMANTIC.ERROR}
            />
          </button>
        )}
        autofocus={false}
      />
    </div>
  );
};
